import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ROLE_LABELS, ROUTES } from '@/constants';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Breadcrumb } from '@/components/ui/breadcrumb';
import { SearchInput } from '@/components/ui/search-input';
import { ThemeSwitcher } from './ThemeSwitcher';
import NotificationBell from './NotificationBell';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

function AdminHeader({
  user,
  breadcrumbs = [],
  onLogout,
  onSearch,
  onMenuClick,
  className,
  showSearch = true,
}) {
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const initials = user
    ? `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase()
    : 'VP';

  const pageTitle = breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1].label : '';

  return (
    <header
      className={cn(
        'sticky top-0 z-40 flex shrink-0 items-center justify-between gap-2 border-b border-border bg-card/80 px-4 backdrop-blur-xl lg:gap-4 lg:px-6',
        'h-[calc(3.5rem+env(safe-area-inset-top))] lg:h-[calc(var(--header-height)+env(safe-area-inset-top))]',
        'pt-[env(safe-area-inset-top)]',
        className
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2 lg:gap-6">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="shrink-0 lg:hidden"
          onClick={onMenuClick}
          aria-label="Open navigation menu"
        >
          <Menu className="h-5 w-5" />
        </Button>

        {pageTitle && (
          <p className="truncate font-display text-base font-semibold md:hidden">{pageTitle}</p>
        )}

        {breadcrumbs.length > 0 && <Breadcrumb items={breadcrumbs} className="hidden md:flex" />}
      </div>

      {searchOpen && (
        <div className="absolute inset-x-0 top-full z-50 border-b border-border bg-card p-3 shadow-md lg:hidden">
          <div className="flex items-center gap-2">
            <SearchInput
              className="flex-1"
              placeholder="Search..."
              onChange={onSearch}
              autoFocus
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setSearchOpen(false)}
              aria-label="Close search"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>
      )}

      <div className="flex shrink-0 items-center gap-1 sm:gap-2 lg:gap-3">
        {showSearch && (
          <div className="hidden w-64 lg:block">
            <SearchInput placeholder="Search..." onChange={onSearch} />
          </div>
        )}

        {showSearch && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="lg:hidden"
            aria-label="Search"
            onClick={() => setSearchOpen((open) => !open)}
          >
            <Search className="h-5 w-5" />
          </Button>
        )}

        <ThemeSwitcher />

        <NotificationBell />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex min-h-12 items-center gap-2 rounded-lg p-1.5 transition-colors hover:bg-muted sm:gap-3"
            >
              <Avatar className="h-8 w-8 shrink-0">
                <AvatarFallback>{initials}</AvatarFallback>
              </Avatar>
              {user && (
                <div className="hidden text-left sm:block">
                  <p className="text-sm font-medium leading-none">
                    {user.firstName} {user.lastName}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {ROLE_LABELS[user.role] || user.role}
                  </p>
                </div>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel>My Account</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate(ROUTES.SETTINGS_PROFILE)}>
              Profile
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate(ROUTES.SETTINGS)}>
              Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onLogout} className="text-error focus:text-error">
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

export default AdminHeader;
